import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, Pencil, Save } from 'lucide-react';
import { toast } from 'sonner';

import { ShelfStatusBodyStatus, useSubmission } from '@/api/generated';
import { isShelfCompleted } from '@/components/blocks/shelves/shelve-card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ShelfStatusControlsProps {
  customerId: string;
  submissionId: string;
  shelveId: string;
  status: string;
  setStatus: (next: ShelfStatusBodyStatus) => Promise<boolean>;
  isPending: boolean;
}

const EDITABLE_STATUSES = Object.values(ShelfStatusBodyStatus);

const isEditableStatus = (status: string): status is ShelfStatusBodyStatus =>
  (EDITABLE_STATUSES as Array<string>).includes(status);

export const ShelfStatusControls: FC<ShelfStatusControlsProps> = ({
  customerId,
  submissionId,
  shelveId,
  status,
  setStatus,
  isPending,
}: ShelfStatusControlsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: submission } = useSubmission(submissionId);

  const isCompleted = status === ShelfStatusBodyStatus.annotation_completed;

  // The next remaining shelf after this one by id, wrapping to the first.
  const nextShelveId = useMemo(() => {
    const shelves = submission?.shelves ?? [];
    const index = shelves.findIndex((shelf) => shelf.id === shelveId);
    const ordered = [...shelves.slice(index + 1), ...shelves.slice(0, index)];
    return (
      ordered.find((shelf) => shelf.id !== shelveId && !isShelfCompleted(shelf))
        ?.id ?? null
    );
  }, [submission, shelveId]);

  const saveAndClose = useCallback(async () => {
    if (!(await setStatus(ShelfStatusBodyStatus.annotation_completed))) return;

    await navigate({
      to: '/app/shelves/$customerId/$submissionId',
      params: { customerId, submissionId },
    });
  }, [customerId, navigate, setStatus, submissionId]);

  const saveAndNext = useCallback(async () => {
    if (!(await setStatus(ShelfStatusBodyStatus.annotation_completed))) return;

    if (nextShelveId) {
      await navigate({
        to: '/app/shelves/$customerId/$submissionId/$shelveId',
        params: { customerId, submissionId, shelveId: nextShelveId },
      });
      return;
    }

    toast.info(t('shelfDetail.controls.noNextShelf'));
    await navigate({
      to: '/app/shelves/$customerId/$submissionId',
      params: { customerId, submissionId },
    });
  }, [customerId, navigate, nextShelveId, setStatus, submissionId, t]);

  return (
    <div className='flex items-center gap-2'>
      <Select
        value={isEditableStatus(status) ? status : undefined}
        onValueChange={async (value) =>
          setStatus(value as ShelfStatusBodyStatus)
        }
        disabled={isPending}>
        <SelectTrigger
          data-testid='shelf-status-select'
          size='sm'
          className='min-w-32'
          aria-label={t('shelfDetail.status.label')}>
          <SelectValue placeholder={status} />
        </SelectTrigger>
        <SelectContent>
          {EDITABLE_STATUSES.map((value) => (
            <SelectItem
              key={value}
              value={value}>
              {t(`shelfDetail.status.${value}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {isCompleted ? (
        <Button
          data-testid='shelf-reedit'
          size='sm'
          variant='outline'
          disabled={isPending}
          onClick={async () =>
            setStatus(ShelfStatusBodyStatus.annotation_in_progress)
          }>
          <Pencil />
          {t('shelfDetail.controls.reEdit')}
        </Button>
      ) : (
        <>
          <Button
            data-testid='shelf-save-close'
            size='sm'
            variant='outline'
            disabled={isPending}
            onClick={saveAndClose}>
            <Save />
            {t('shelfDetail.controls.saveAndClose')}
          </Button>
          <Button
            data-testid='shelf-save-next'
            size='sm'
            disabled={isPending}
            onClick={saveAndNext}>
            {t('shelfDetail.controls.saveAndNext')}
            <ArrowRight />
          </Button>
        </>
      )}
    </div>
  );
};
